import React from "react";
import {
  Easing,
  interpolate,
  interpolateColors,
  spring,
  useCurrentFrame,
} from "remotion";
import { theme } from "../../brand";
import { FLOAT_SHADOW, LOOK } from "../../b2c/look";
import { Words } from "../../b2c/ui/Words";
import { REEL, ReelFrame } from "../ui/ReelFrame";
import { QUIZ } from "./copy";

/**
 * Reel "Vrai ou faux" (9:16, silent, ~14.3s): three claims about getting
 * paid to recommend, one after the other, each with a countdown and
 * VRAI / FAUX buttons. On the reveal the right button lights up, a false
 * claim is struck out, and the claim shrinks to make room for the FAQ
 * answer in the same card, as big as the claim was: the eye stays on one
 * spot and the last thing it reads is the correct statement. Two FAUX,
 * then a VRAI (cash to your bank) as the twist.
 * Answers the newcomer's objections, unlike the other reels, which all
 * say "you already recommend for free".
 *
 * Frame 0 is the cover (hook + question 1 + buttons): a question people
 * stop to answer. Silent: the sound is added in Instagram.
 */

/** Timeline, in frames. Per question, `q` frames; the rest is local to it. */
const T = {
  q: 110,
  /** Countdown, then the answer lights up (and a false claim is struck). */
  timer: [4, 36],
  reveal: 38,
  /** The claim shrinks, then the answer rises under it. */
  shrink: 44,
  why: 46,
  /** Right after the last question: its answer holds as long as the others. */
  exit: 330,
  end: 342,
  duration: 430,
} as const;

export const QUIZ_DURATION = T.duration;

const N = QUIZ.items.length;
if (T.exit < N * T.q) {
  throw new Error("Quiz reel: T.exit must come after the last question.");
}

const GREEN = theme.brand.colors.success[700];
const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;
const ramp = (frame: number, at: number, len: number, ease = Easing.out(Easing.cubic)) =>
  interpolate(frame, [at, at + len], [0, 1], { ...clamp, easing: ease });

/** Layout (logical px). Centred, clear of the Reels rail on the right. */
const X = 140;
const WIDTH = 760;
const Y = { pips: 460, card: 524, cardH: 520, buttons: 1090 };
/** Text width inside the card. */
const TEXT = WIDTH - 96;

/** Where question `i` is: 0 → 1 entering, then 0 → 1 leaving. */
const phase = (frame: number, i: number) => {
  const local = frame - i * T.q;
  const enter = i === 0 ? 1 : ramp(local, 0, 10);
  const leave = i === N - 1 ? 0 : ramp(local, T.q - 8, 8, Easing.in(Easing.cubic));
  return { local, enter, leave, on: local >= 0 && (i === N - 1 || local < T.q) };
};

const Check: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
    <path
      d="M4 12.5l5 5L20 6.5"
      fill="none"
      stroke={color}
      strokeWidth={3.2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Pips: React.FC<{ frame: number }> = ({ frame }) => (
  <div
    style={{
      position: "absolute",
      top: Y.pips,
      left: X,
      width: WIDTH,
      display: "flex",
      justifyContent: "center",
      gap: 18,
    }}
  >
    {QUIZ.items.map((_, k) => {
      const done = spring({
        frame: frame - (k * T.q + T.reveal),
        fps: REEL.fps,
        config: { damping: 12, stiffness: 220, mass: 0.6 },
      });
      const current = Math.floor(frame / T.q) === k;
      return (
        <div
          key={k}
          style={{
            width: 34,
            height: 34,
            borderRadius: 17,
            boxSizing: "border-box",
            border: `3px solid ${current || done > 0 ? LOOK.white : LOOK.blueSoft}`,
            background: done > 0 ? GREEN : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            scale: 1 + 0.25 * Math.sin(Math.PI * Math.min(done, 1)),
          }}
        >
          {done > 0 ? <Check size={22} color={LOOK.white} /> : null}
        </div>
      );
    })}
  </div>
);

/** A claim struck out word by word, left to right (wraps like plain text). */
const StrikeWords: React.FC<{ text: string; at: number; frame: number }> = ({
  text,
  at,
  frame,
}) => (
  <>
    {text.split(" ").map((word, k) => (
      <React.Fragment key={k}>
        {k > 0 ? " " : null}
        <span style={{ position: "relative", display: "inline-block" }}>
          {word}
          <span
            style={{
              position: "absolute",
              left: "-3%",
              top: "52%",
              height: 8,
              marginTop: -4,
              width: `${ramp(frame, at + k * 1.5, 5) * 106}%`,
              borderRadius: 4,
              background: LOOK.red,
            }}
          />
        </span>
      </React.Fragment>
    ))}
  </>
);

const Question: React.FC<{ i: number; frame: number }> = ({ i, frame }) => {
  const { local, enter, leave, on } = phase(frame, i);
  if (!on) return null;
  const item = QUIZ.items[i];
  const left = 1 - ramp(local, T.timer[0], T.timer[1] - T.timer[0], Easing.linear);
  const decided = ramp(local, T.reveal, 8);
  const shrink = ramp(local, T.shrink, 12, Easing.inOut(Easing.cubic));
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        padding: "34px 48px 40px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        opacity: enter * (1 - leave),
        translate: `${(1 - enter) * 160 - leave * 160}px 0`,
      }}
    >
      <div
        style={{
          fontFamily: LOOK.body,
          fontWeight: LOOK.semi,
          fontSize: 26,
          letterSpacing: 4,
          textTransform: "uppercase",
          color: LOOK.muted,
        }}
      >
        {QUIZ.question} {i + 1}/{N}
      </div>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* The claim: struck if false (green if true), then shrunk. `zoom`
            keeps its line breaks while it shrinks (its width is fixed). */}
        <div
          style={{
            width: TEXT,
            zoom: 1 - 0.42 * shrink,
            fontFamily: LOOK.display,
            fontWeight: LOOK.bold,
            fontSize: 72,
            lineHeight: 1.08,
            letterSpacing: -2,
            color: interpolateColors(
              decided,
              [0, 1],
              [LOOK.ink, item.truth ? GREEN : LOOK.muted],
            ),
            textAlign: "center",
            textWrap: "balance",
          }}
        >
          {item.truth ? (
            item.claim
          ) : (
            <StrikeWords text={item.claim} at={i * T.q + T.reveal} frame={frame} />
          )}
        </div>
        {/* The answer opens under it, as big as the claim was. */}
        <div
          style={{
            maxHeight: shrink * 320,
            marginTop: shrink * 26,
            overflow: "hidden",
          }}
        >
          {local >= T.why ? (
            <Words
              text={item.why}
              start={i * T.q + T.why}
              stagger={3}
              fontSize={70}
              color={LOOK.ink}
              style={{ width: TEXT }}
            />
          ) : null}
        </div>
      </div>
      {/* Countdown: empties before the answer lights up, then goes. */}
      <div
        style={{
          alignSelf: "stretch",
          height: 12,
          borderRadius: 6,
          background: LOOK.sky,
          overflow: "hidden",
          opacity: 1 - decided,
        }}
      >
        <div
          style={{
            width: `${left * 100}%`,
            height: "100%",
            borderRadius: 6,
            background: LOOK.blue,
          }}
        />
      </div>
    </div>
  );
};

const Button: React.FC<{ label: string; lit: number; dim: number }> = ({
  label,
  lit,
  dim,
}) => (
  <div
    style={{
      flex: 1,
      height: 150,
      borderRadius: 75,
      background: lit > 0.5 ? GREEN : LOOK.white,
      color: lit > 0.5 ? LOOK.white : LOOK.ink,
      boxShadow: FLOAT_SHADOW,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 18,
      fontFamily: LOOK.display,
      fontWeight: LOOK.bold,
      fontSize: 64,
      letterSpacing: 1,
      opacity: 1 - 0.65 * dim,
      scale: 1 + 0.1 * Math.sin(Math.PI * Math.min(lit, 1)),
    }}
  >
    {lit > 0.5 ? <Check size={52} color={LOOK.white} /> : null}
    {label}
  </div>
);

const Buttons: React.FC<{ frame: number }> = ({ frame }) => {
  const i = Math.min(Math.floor(frame / T.q), N - 1);
  const { local, leave } = phase(frame, i);
  const lit =
    spring({
      frame: local - T.reveal,
      fps: REEL.fps,
      config: { damping: 11, stiffness: 240, mass: 0.6 },
    }) * (1 - leave);
  const dim = ramp(local, T.reveal, 6) * (1 - leave);
  const truth = QUIZ.items[i].truth;
  return (
    <div
      style={{
        position: "absolute",
        top: Y.buttons,
        left: X,
        width: WIDTH,
        display: "flex",
        gap: 32,
      }}
    >
      <Button label={QUIZ.yes} lit={truth ? lit : 0} dim={truth ? 0 : dim} />
      <Button label={QUIZ.no} lit={truth ? 0 : lit} dim={truth ? dim : 0} />
    </div>
  );
};

export const QuizReel: React.FC = () => {
  const frame = useCurrentFrame();
  // Exit: the whole quiz drops out before the end card.
  const drop = interpolate(frame, [T.exit, T.exit + 16], [0, 1500], {
    ...clamp,
    easing: Easing.in(Easing.cubic),
  });

  return (
    <ReelFrame
      hook={QUIZ.hook}
      exitAt={T.exit}
      end={{ at: T.end, line: QUIZ.end.line, sub: QUIZ.end.sub }}
    >
      <div style={{ position: "absolute", inset: 0, translate: `0 ${drop}px` }}>
        <Pips frame={frame} />
        <div
          style={{
            position: "absolute",
            top: Y.card,
            left: X,
            width: WIDTH,
            height: Y.cardH,
            borderRadius: 40,
            background: LOOK.white,
            boxShadow: FLOAT_SHADOW,
            overflow: "hidden",
          }}
        >
          {QUIZ.items.map((item, i) => (
            <Question key={item.claim} i={i} frame={frame} />
          ))}
        </div>
        <Buttons frame={frame} />
      </div>
    </ReelFrame>
  );
};
